import React, { memo, useCallback } from "react";
import propTypes from "prop-types";
import cx from "classnames";
import styles from "./ExpansionPanel.module.scss";
import ExpansionPanelCtx from "./ExpansionPanelCtx";
import Header, { ICON_POSITION } from "./Header";
import Content from "./Content";
import useControlled from "../../hooks/useControlled";

const ExpansionPanel = ({
  children,
  data,
  expanded: expandedProp,
  defaultExpanded,
  onChange,
  mountOnEnter,
  unmountOnExit,
  disabled,
  className
}) => {
  const [expanded, setExpanded] = useControlled({
    controlled: expandedProp,
    default: defaultExpanded,
    name: "ExpansionPanel"
  });

  const toggleExpansion = useCallback(
    event => {
      setExpanded(!expanded);

      if (onChange) {
        onChange(event, !expanded);
      }
    },
    [setExpanded, expanded]
  );

  const renderHeader = useCallback(({ header }) => {
    if (!header.title) {
      return <Header>{header}</Header>;
    }

    return (
      <Header expandIcon={header.expandIcon} iconPosition={header.iconPosition}>
        {header.title}
      </Header>
    );
  }, []);

  if (!children && !data) {
    return null;
  }

  return (
    <div className={cx(styles.container, { [styles.disabled]: disabled }, className)}>
      <ExpansionPanelCtx.Provider
        value={{
          expanded,
          mountOnEnter,
          unmountOnExit,
          disabled,
          toggleExpansion,
          onChange
        }}
      >
        {children || (
          <>
            {renderHeader(data)}
            <Content>{data.content}</Content>
          </>
        )}
      </ExpansionPanelCtx.Provider>
    </div>
  );
};

ExpansionPanel.defaultProps = {
  mountOnEnter: false,
  unmountOnExit: false,
  expanded: undefined,
  defaultExpanded: false,
  disabled: false,
  className: undefined,
  children: null,
  data: null,
  onChange: () => {}
};

ExpansionPanel.propTypes = {
  /** Callback function for state changes. */
  onChange: propTypes.func,
  /** Data for rendering ExpansionPanel */
  data: propTypes.shape({
    header: propTypes.oneOfType([
      propTypes.node.isRequired,
      propTypes.shape({
        title: propTypes.node.isRequired,
        expandIcon: propTypes.node,
        iconPosition: propTypes.oneOf(ICON_POSITION)
      })
    ]).isRequired,
    content: propTypes.node.isRequired
  }),
  children: propTypes.node,
  /** Whether render content if not expanded. */
  mountOnEnter: propTypes.bool,
  /** Unmount component on close. */
  unmountOnExit: propTypes.bool,
  /** Toggle ExpansionPanel expansion and if it's true, than it become controlled. */
  expanded: propTypes.bool,
  /** Default value for expanded */
  defaultExpanded: propTypes.bool,
  /** Make panel disabled */
  disabled: propTypes.bool,
  /** ClassName for overriding default styles */
  className: propTypes.string
};

export default memo(ExpansionPanel);
